import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// For free-trial tenants: overlay a repeating "Free Trial" watermark and deter
// casual copying (copy/cut/right-click blocked outside form fields). This is a
// deterrent, not DRM — screenshots/devtools can't be prevented client-side.
const WATERMARK_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='200'>
     <text x='30' y='120' font-family='Arial, sans-serif' font-size='26' font-weight='700'
           fill='rgba(15,23,42,0.10)' transform='rotate(-28 160 100)'>Free Trial</text>
   </svg>`
);

export const TrialProtection = () => {
  const { user } = useAuth();
  const [isTrial, setIsTrial] = useState(false);

  const check = useCallback(async () => {
    if (!user) { setIsTrial(false); return; }
    try {
      const { data } = await api.get("/tenant/me");
      setIsTrial(data?.plan === "free_trial");
    } catch (e) { setIsTrial(false); }
  }, [user]);

  useEffect(() => {
    check();
    const h = () => check();
    window.addEventListener("claro:tenant-updated", h);
    window.addEventListener("claro:registrations-changed", h);
    return () => {
      window.removeEventListener("claro:tenant-updated", h);
      window.removeEventListener("claro:registrations-changed", h);
    };
  }, [check]);

  useEffect(() => {
    if (!isTrial) {
      document.body.classList.remove("trial-locked");
      return;
    }
    document.body.classList.add("trial-locked");
    const isEditable = (el) =>
      el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    // Trial rule: NEVER allow copying/cutting content out of the app, and never allow
    // right-click. Paste is still permitted INTO form fields so trial users can fill
    // out inputs (e.g. paste their own email) — pasting onto page content is blocked.
    const blockAlways = (e) => e.preventDefault();
    const blockPaste = (e) => { if (!isEditable(e.target)) e.preventDefault(); };
    document.addEventListener("copy", blockAlways);
    document.addEventListener("cut", blockAlways);
    document.addEventListener("paste", blockPaste);
    document.addEventListener("contextmenu", blockAlways);
    return () => {
      document.body.classList.remove("trial-locked");
      document.removeEventListener("copy", blockAlways);
      document.removeEventListener("cut", blockAlways);
      document.removeEventListener("paste", blockPaste);
      document.removeEventListener("contextmenu", blockAlways);
    };
  }, [isTrial]);

  if (!isTrial) return null;

  return (
    <div
      data-testid="trial-watermark"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 45,
        pointerEvents: "none",
        backgroundImage: `url("data:image/svg+xml,${WATERMARK_SVG}")`,
        backgroundRepeat: "repeat",
      }}
    />
  );
};

export default TrialProtection;
